import React, { useEffect, useState } from "react";
import { alpacaApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import { Zap, ShieldCheck, Unplug, ExternalLink } from "lucide-react";

export default function AlpacaConnect() {
  const [state, setState] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [mode, setMode] = useState("paper");
  const [mirror, setMirror] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setState(await alpacaApi.status()); } catch (err) { console.debug("[AlpacaConnect] status fetch failed", err); }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!apiKey.trim() || !apiSecret.trim()) return toast.error("Enter API key and secret");
    if (mode === "live" && !state?.live_trading_enabled) {
      return toast.error("Live trading is disabled on this server — contact admin");
    }
    if (mode === "live") {
      const ok = window.confirm(
        "⚠ LIVE MODE — real money will be traded by the bot.\n\n" +
        "By clicking OK you confirm:\n" +
        "• You understand trading is high-risk and you can lose your capital.\n" +
        "• You set the buying-power at Alpaca — TradingoX cannot exceed it.\n" +
        "• You will monitor the bot and revoke API keys at Alpaca any time.\n\n" +
        "Continue in LIVE mode?"
      );
      if (!ok) return;
    }
    try {
      setBusy(true);
      await alpacaApi.connect({ api_key: apiKey.trim(), api_secret: apiSecret.trim(), mode, mirror_bot_trades: mirror });
      setApiKey(""); setApiSecret("");
      toast.success(`Alpaca ${mode} connected — bot trades will mirror`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connect failed");
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    try { await alpacaApi.disconnect(); toast("Alpaca disconnected"); await load(); }
    catch (err) { console.error("[AlpacaConnect] disconnect failed", err); toast.error("Disconnect failed"); }
  };

  const connected = !!state?.connected;

  return (
    <div className="aq-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Broker</div>
          <div className="font-display text-lg font-bold mt-0.5 flex items-center gap-2">
            <Zap className="w-4 h-4 text-emerald-400" /> Alpaca
          </div>
        </div>
        {connected && (
          <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border ${state.mode === "live" ? "bg-rose-500/15 text-rose-300 border-rose-500/30" : "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"}`}>
            {state.mode} live
          </span>
        )}
      </div>

      {!connected ? (
        <>
          <div className="rounded-md border border-emerald-500/25 bg-emerald-500/5 p-2.5 mb-3">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-300 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Broker holds your funds
            </div>
            <p className="mt-1 text-[11px] text-slate-300 leading-snug">
              TradingoX never holds or moves money. Your broker is the sole custodian; set the buying-power we can trade with at your broker,
              and handle all deposits & withdrawals there too.
            </p>
          </div>
          <p className="text-xs text-slate-400 mb-3 leading-relaxed">
            Route the scalping bot to a real Alpaca account. Start in <b className="text-emerald-300">paper mode</b> — same API, zero risk.
          </p>
          <div className="space-y-2">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">API Key</label>
              <Input data-testid={TID.alpacaKeyInput} value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                placeholder="PKXXXXXXXXXXXXXXXXXX" className="mt-1 bg-[#0D0E14] border-white/10 font-mono text-xs" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Secret Key</label>
              <Input data-testid={TID.alpacaSecretInput} type="password" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)}
                placeholder="Secret" className="mt-1 bg-[#0D0E14] border-white/10 font-mono text-xs" />
            </div>
            <div className="flex gap-2 items-center">
              <select data-testid={TID.alpacaModeSelect} value={mode} onChange={(e) => setMode(e.target.value)}
                className="flex-1 h-9 rounded-md bg-[#0D0E14] border border-white/10 px-2 font-mono text-xs">
                <option value="paper">paper (safe)</option>
                <option value="live" disabled={!state?.live_trading_enabled}>
                  live {state?.live_trading_enabled ? "(real money)" : "(disabled)"}
                </option>
              </select>
              <label className="flex items-center gap-2 text-xs text-slate-400">
                <Switch data-testid={TID.alpacaMirrorToggle} checked={mirror} onCheckedChange={setMirror} />
                Mirror bot
              </label>
            </div>
            <Button data-testid={TID.alpacaConnectBtn} onClick={connect} disabled={busy}
              className="w-full h-10 mt-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
              {busy ? "Connecting…" : "Connect Alpaca"}
            </Button>
            <a href="https://app.alpaca.markets/paper/dashboard/overview" target="_blank" rel="noreferrer"
              className="mt-2 flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-300 transition">
              <ExternalLink className="w-3 h-3" /> Get free Alpaca paper API keys
            </a>
          </div>
        </>
      ) : (
        <>
          <div className="rounded-md bg-black/40 border border-white/5 p-3 space-y-1">
            <div className="flex items-center gap-2 text-xs text-emerald-300 font-mono">
              <ShieldCheck className="w-3.5 h-3.5" /> {state.account?.status || "connected"}
            </div>
            <Row k="Buying power" v={`$${(state.account?.buying_power || 0).toLocaleString()}`} />
            <Row k="Cash" v={`$${(state.account?.cash || 0).toLocaleString()}`} />
            <Row k="Portfolio" v={`$${(state.account?.portfolio_value || 0).toLocaleString()}`} />
            <Row k="Positions" v={`${state.positions?.length || 0}`} />
            <Row k="Bot mirroring" v={state.mirror_bot_trades ? "ON" : "OFF"} />
          </div>
          <Button data-testid={TID.alpacaDisconnectBtn} onClick={disconnect} variant="ghost"
            className="w-full mt-3 h-9 text-slate-400 hover:text-white hover:bg-white/10">
            <Unplug className="w-3.5 h-3.5 mr-1.5" /> Disconnect
          </Button>
        </>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-slate-500 font-mono">{k}</span>
      <span className="font-mono font-semibold text-slate-200">{v}</span>
    </div>
  );
}
